import React, { useEffect, useState } from 'react';

import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { IconCheckCircle, IconError, IconXCircle } from '@wds/icons';
import { Typography } from '@wds/typography';
import { Button } from '@wds/button';
import { Popout } from '@wds/popout';
import { Select } from '@wds/select';

import PropTypes from 'prop-types';
import STRINGS from '../../../../sma.admin/constants';

const EditableSelectField = (props) => {
	const {
		id, fields, label, editable, state, setState, reset, editingFields,
		setEditingFields,
	} = props;
	const [tempState, setTempState] = useState({});
	const [editing, setEditing] = useState(false);
	const [displayText, setDisplayText] = useState('');
	const [fieldErrors, setFieldErrors] = useState({});
	const [originalState, setOriginalState] = useState({});
	const [changed, setChanged] = useState(false);
	const [disableSave, setDisableSave] = useState(true);

	useEffect(() => {
		if (editing) {
			const newEditingFields = { ...editingFields, [id]: true };
			setEditingFields(newEditingFields);
		} else {
			const editingFieldsCopy = { ...editingFields };
			delete editingFieldsCopy[id];
			setEditingFields(editingFieldsCopy);
		}
	}, [editing]);

	useEffect(() => {
		if (Object.keys(originalState).length === 0) {
			handleSetOriginalState();
		}
		resetState();
	}, [fields]);

	useEffect(() => {
		if (reset) {
			handleSetOriginalState();
		}
	}, [reset]);

	useEffect(() => {
		let unlockSubmit = true;
		if (Object.keys(tempState).length > 0 && JSON.stringify(tempState) !== JSON.stringify(originalState)) {
			setChanged(true);
			fields.forEach((field) => {
				const isFieldChanged = tempState[field.id] !== originalState[field.id];
				const isFieldError = fieldErrors[field.id];
				if (!isFieldChanged || isFieldError) {
					unlockSubmit = false;
				}
			});
		} else {
			unlockSubmit = false;
		}
		setDisableSave(!unlockSubmit);
	}, [tempState]);

	const handleChangeField = (e) => {
		const { id: fieldId, value } = e.target;
		setTempState({ ...tempState, [fieldId]: value });
	};

	const resetState = () => {
		let newDisplayText = '';
		let newTempState = { ...tempState };
		fields.forEach((field) => {
			newTempState = { ...newTempState, [field.id]: state[field.id] };
			newDisplayText += field.options[field.defaultValue] || field.defaultValue || ' ';
		});
		setDisplayText(newDisplayText);
		setTempState(newTempState);
		setChanged(false);
	};

	const validateFields = () => {
		const fieldErrorsObj = {};
		fields.forEach((field) => {
			fieldErrorsObj[field.id] = !!(field.isRequired && !tempState[field.id]);
		});
		setFieldErrors(fieldErrorsObj);
		return fieldErrorsObj;
	};

	const handleUpdate = () => {
		const fieldErrorsObj = validateFields();
		if (Object.values(fieldErrorsObj).some((error) => error)) {
			return;
		}
		setState({
			...state,
			...tempState,
		});
		setEditing(false);
	};

	const handleCancel = () => {
		let tempStateCopy = {};
		fields.forEach((field) => {
			tempStateCopy = { ...tempStateCopy, [field.id]: state[field.id] };
		});
		setTempState(tempStateCopy);
		resetState();
		setEditing(false);
	};

	const handleClear = () => {
		setState({
			...state,
			...originalState,
		});
		resetState();
	};

	const handleSetOriginalState = () => {
		let originalFields = {};
		fields.forEach((field) => {
			originalFields = { ...originalFields, [field.id]: field.defaultValue };
		});
		setOriginalState(originalFields);
		resetState();
	};

	function getFieldOptions(options) {
		if (Array.isArray(options)) {
			return options.map((option) => (
				<Select.Option key={option} value={option} role='option' aria-label={option}>{option}</Select.Option>
			));
		}
		return Object.entries(options).map((option) => (
			<Select.Option
				key={option[0]}
				value={option[0]}
				role='option'
				aria-label={option[1]}
			>
				{option[1]}
			</Select.Option>
		));
	}

	return (
		<Box aria-label={id} id={id} role='region'>
			<Box display='flex' flexDirection='column' gap={2} role='region'>
				<Box display='flex' gap={2} role='region'>
					<Box display='flex' justifyContent='center' gap={2} role='region'>
						<Typography variant='bodySm' weight='medium'>{label}</Typography>
					</Box>
					{!editing && (
						<Box display='flex' justifyContent='center' gap={2} role='region'>
							<Box
								onClick={editable ? () => setEditing(true) : undefined}
								tabIndex='0'
								aria-label={STRINGS.labels.showChangeFields}
								role='button'
							>
								<Typography
									variant='bodySm'
									weight='medium'
									href={editable ? '#' : undefined}
									color={editable ? 'link' : 'text'}
									style={{ cursor: editable ? 'pointer' : 'default' }}
								>
									{STRINGS.change}
								</Typography>
							</Box>
							<Box display='flex' justifyContent='space-between' gap={2}>
								{!reset && changed && (
									<>
										<Typography
											variant='bodySm'
											weight='medium'
											color='warning'
										>
											{STRINGS.unsavedChanges}
										</Typography>
										<Box display='flex' alignItems='center'>
											<IconXCircle
												size='sm'
												color='warning'
												onClick={handleClear}
											/>
										</Box>
									</>
								)}
							</Box>
						</Box>
					)}
				</Box>
				{!editing && (
					<Typography
						component='span'
						id={`sma-ef-${id}-display-text`}
						variant='bodySm'
						tabIndex='0'
						aria-label={`${id} ${STRINGS.labels.displayText}`}
					>
						{displayText}
					</Typography>
				)}
			</Box>
			<Box width='100%'>
				<Grid noBleeds>
					{fields.map((field) => (
						editing ? (
							<Grid.Item xs={fields.length < 2 ? 12 : 6} key={field.id}>
								<Box
									display='flex'
									justifyContent='left'
									gap={2}
									key={field.id}
									style={{ marginTop: '7px' }}
								>
									<Select
										defaultValue={field.defaultValue}
										size='xs'
										id={field.id}
										onChange={handleChangeField}
										stretched
									>
										{getFieldOptions(field.options)}
									</Select>
									{fieldErrors[field.id] && (
										<Popout
											show={fieldErrors[field.id]}
											clickable
											trigger={(
												<IconError
													title='error'
													color='error'
													tabIndex={0}
												/>
											)}
										>
											<Popout.Body>
												{field.errorMessage ? field.errorMessage : STRINGS.fieldErrors.text}
											</Popout.Body>
										</Popout>
									)}
								</Box>
							</Grid.Item>
						) : undefined
					))}
				</Grid>
			</Box>
			{editing && (
				<Box display='flex' justifyContent='flex-end'>
					<Button
						label='cancel'
						color='warning'
						size='sm'
						variant='text'
						onClick={editable ? () => handleCancel() : undefined}
					>
						<IconXCircle
							size='sm'
							color='danger'
						/>
					</Button>
					<Button
						label='save'
						color='primary'
						size='sm'
						variant='text'
						onClick={editable ? () => handleUpdate() : undefined}
						disabled={disableSave}
					>
						<IconCheckCircle
							size='sm'
							color='primary'
						/>
					</Button>
				</Box>
			)}
		</Box>
	);
};

EditableSelectField.propTypes = {
	id: PropTypes.string.isRequired,
	fields: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			type: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			isRequired: PropTypes.bool,
			errorMessage: PropTypes.string,
			defaultValue: PropTypes.string,
			options: PropTypes.oneOfType([
				PropTypes.objectOf(PropTypes.string),
				PropTypes.arrayOf(PropTypes.string),
			]),
		}),
	).isRequired,
	label: PropTypes.string.isRequired,
	editable: PropTypes.bool,
	state: PropTypes.object.isRequired,
	setState: PropTypes.func.isRequired,
	reset: PropTypes.bool.isRequired,
	editingFields: PropTypes.object,
	setEditingFields: PropTypes.func,
};
EditableSelectField.defaultProps = {
	editable: true,
	editingFields: {},
	setEditingFields: () => {
	},
};

export default EditableSelectField;
